<template>
  <div class="dashboard-container">
    <!-- Dashboard Header -->
    <v-card class="dashboard-header mb-6" flat>
      <v-card-text class="d-flex align-center py-5 px-6">
        <div>
          <div class="text-h5 font-weight-bold white--text" style="letter-spacing: -0.5px;">
            {{ greeting }}
          </div>
          <div class="text-body-2 white--text" style="opacity: 0.75; margin-top: 2px;">
            {{ currentDate }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          icon
          dark
          style="z-index: 2; background: rgba(255,255,255,0.2);"
          @click="openLibrary"
        >
          <v-icon size="22" color="white">{{ icons.mdiCog }}</v-icon>
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Widget Grid (drag to reorder) -->
    <draggable
      :value="activeWidgets"
      @end="onDragEnd"
      handle=".widget-drag-handle"
      ghost-class="widget-ghost"
      tag="div"
      class="widgets-grid"
    >
      <div
        v-for="widget in activeWidgets"
        :key="widget.id"
        class="widget-col"
        :class="'widget-col--' + widget.size"
      >
        <v-card
          class="widget-card"
          :class="'widget-' + widget.id"
          elevation="0"
        >
          <!-- Title bar — drag handle -->
          <div
            class="widget-title-bar d-flex align-center px-4 py-3 widget-drag-handle"
          >
            <div
              class="widget-icon-dot mr-3"
              :style="{ background: getIconBg(widget.id) }"
            >
              <v-icon size="16" :color="getIconColor(widget.id)">
                {{ widget.icon }}
              </v-icon>
            </div>
            <span class="text-subtitle-2 font-weight-semibold widget-title-text">
              {{ widget.name }}
            </span>
            <v-spacer />
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  class="widget-menu-btn"
                >
                  <v-icon x-small color="grey lighten-1">
                    mdi-dots-horizontal
                  </v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="cycleSize(widget.id)">
                  <v-list-item-icon>
                    <v-icon small>mdi-resize</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Resize</v-list-item-title>
                </v-list-item>
                <v-list-item @click="removeWidget(widget.id)">
                  <v-list-item-icon>
                    <v-icon small color="error">mdi-close</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Remove</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <v-divider style="opacity: 0.4;" />
          <v-card-text class="widget-body pa-5">
            <component :is="widget.component" :ref="widget.id" />
          </v-card-text>
        </v-card>
      </div>
    </draggable>

    <!-- Empty State -->
    <div v-if="activeWidgets.length === 0" class="text-center py-16">
      <v-icon size="72" color="grey lighten-2">
        mdi-view-dashboard-outline
      </v-icon>
      <h3 class="text-h6 grey--text text--lighten-1 mt-4 mb-2">
        Your dashboard is empty
      </h3>
      <p class="text-body-2 grey--text text--lighten-2 mb-4">
        Add widgets to customize your home screen
      </p>
      <v-btn
        color="primary"
        rounded
        depressed
        @click="openLibrary"
      >
        <v-icon left>mdi-plus</v-icon>
        Add Widgets
      </v-btn>
    </div>

    <!-- Widget Library Dialog -->
    <WidgetLibrary />
  </div>
</template>

<script>
// eslint-disable-next-line import/no-unresolved
import draggable from 'vuedraggable';
import { mdiCog } from '@mdi/js';
import { mapGetters, mapMutations } from 'vuex';
import { sizeToCols } from './widgetRegistry';
import WidgetLibrary from './WidgetLibrary.vue';
/* The --widget-* theme vars the widgets and the library dialog are drawn from live here. The
   sheet is not loaded in main.js - every consumer imports it, and it reaches the page through
   vue-style-loader when that consumer's chunk does. Home is the one route that pulls in none of
   the tax/apportionment components that used to be its only importers, so without this line the
   vars are simply absent on the dashboard and every var() falls back to its light value: white
   cards on the dark theme, with no error to say why. This import covers the whole dashboard -
   the widgets are children of this component, and the library dialog detaches to [data-app],
   which is the same .v-application element the vars are declared on. */
import '../../assets/style.css';

/* `bg`/`color` are the light-theme pair. `dark` is the same hue lifted to a shade that carries
   on #1e1e1e - the light icon colours are 500-800 shades and go near-invisible there. The dark
   dot fill is derived from it (a translucent wash, see iconBgDark) rather than listed, so the
   dot and the glyph inside it can never drift to different hues. */
const ICON_META = {
  'verse-of-the-day': { bg: '#fdf0e0', color: '#e65100', dark: '#ffb74d' },
  announcements: { bg: '#e8f0fe', color: '#1565c0', dark: '#64b5f6' },
  'whats-new': { bg: '#e8f5e9', color: '#2e7d32', dark: '#81c784' },
  'unposted-payments': { bg: '#fff3e0', color: '#e65100', dark: '#ffb74d' },
  'quick-nav': { bg: '#ede7f6', color: '#512da8', dark: '#b39ddb' },
  'transaction-volume': { bg: '#e0f2f1', color: '#00695c', dark: '#4db6ac' },
  'web-payment-queue': { bg: '#fff8e1', color: '#f57c00', dark: '#ffd54f' },
  'cash-drawer': { bg: '#e8f5e9', color: '#2e7d32', dark: '#81c784' },
  'gl-balance-status': { bg: '#e0f7fa', color: '#00838f', dark: '#4dd0e1' },
  'collections-by-category': { bg: '#ede7f6', color: '#4527a0', dark: '#9fa8da' },
  'eom-readiness': { bg: '#eceff1', color: '#455a64', dark: '#b0bec5' },
};

// #rrggbb -> rgba() at the given alpha, so a dot fill can be mixed from its own icon colour.
function hexToRgba(hex, alpha) {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default {
  name: 'UserDashboard',
  components: {
    WidgetLibrary,
    draggable,
  },
  data: () => ({ icons: { mdiCog } }),
  computed: {
    ...mapGetters('dashboard', ['activeWidgets']),
    isDark() {
      return this.$vuetify.theme.dark;
    },
    greeting() {
      const hour = new Date().getHours();
      if (hour < 12) return 'Good morning';
      if (hour < 17) return 'Good afternoon';
      return 'Good evening';
    },
    currentDate() {
      return new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },
  methods: {
    ...mapMutations('dashboard', [
      'setLibraryOpen',
      'toggleWidget',
      'updateWidgetSize',
      'reorderWidgets',
    ]),
    getCols(size) {
      return sizeToCols[size] || 4;
    },
    getIconBg(id) {
      const meta = ICON_META[id];
      if (!meta) return this.isDark ? 'rgba(255, 255, 255, 0.08)' : '#f5f5f5';
      return this.isDark ? hexToRgba(meta.dark, 0.16) : meta.bg;
    },
    getIconColor(id) {
      const meta = ICON_META[id];
      if (!meta) return 'primary';
      return this.isDark ? meta.dark : meta.color;
    },
    openLibrary() {
      this.setLibraryOpen(true);
    },
    removeWidget(widgetId) {
      this.toggleWidget(widgetId);
    },
    cycleSize(widgetId) {
      const sizes = ['sm', 'md', 'lg', 'xl'];
      const w = this.activeWidgets.find((x) => x.id === widgetId);
      if (!w) return;
      const idx = sizes.indexOf(w.size);
      const next = (idx + 1) % sizes.length;
      this.updateWidgetSize({ widgetId, size: sizes[next] });
    },
    onDragEnd(evt) {
      const ids = this.activeWidgets.map((w) => w.id);
      const moved = ids.splice(evt.oldIndex, 1)[0];
      ids.splice(evt.newIndex, 0, moved);
      this.reorderWidgets(ids);
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  padding: 20px 24px 32px;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
}

/* Header */
.dashboard-header {
  background: linear-gradient(135deg, #1565c0 0%, #0d47a1 60%, #1a237e 100%);
  border-radius: 16px !important;
  overflow: hidden;
  position: relative;
}

.dashboard-header::before {
  content: '';
  position: absolute;
  top: -30px;
  right: -20px;
  width: 160px;
  height: 160px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 50%;
  pointer-events: none;
}

.dashboard-header::after {
  content: '';
  position: absolute;
  bottom: -50px;
  right: 80px;
  width: 200px;
  height: 200px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 50%;
  pointer-events: none;
}

/* Widget grid */
.widgets-grid {
  display: flex;
  flex-wrap: wrap;
  margin: -12px;
}

.widget-col {
  padding: 12px;
  box-sizing: border-box;
  width: 100%;
}

/* Responsive column widths by size */
@media (min-width: 600px) {
  .widget-col--sm  { width: 33.333%; }
  .widget-col--md  { width: 50%; }
  .widget-col--lg  { width: 66.666%; }
  .widget-col--xl  { width: 100%; }
}

@media (min-width: 960px) {
  .widget-col--sm  { width: 25%; }
  .widget-col--md  { width: 33.333%; }
  .widget-col--lg  { width: 50%; }
  .widget-col--xl  { width: 100%; }
}

/* Widget card */
.widget-card {
  height: 100%;
  background: var(--widget-surface, #ffffff);
  border-radius: 16px !important;
  box-shadow: var(--widget-shadow, 0 4px 24px rgba(0, 0, 0, 0.06)) !important;
  border: 1px solid var(--widget-border, rgba(0, 0, 0, 0.04));
  transition: box-shadow 0.2s ease, transform 0.15s ease;
  overflow: hidden;
}

.widget-card:hover {
  box-shadow: var(--widget-shadow-hover, 0 8px 40px rgba(0, 0, 0, 0.10)) !important;
  transform: translateY(-2px);
}

/* Title bar */
.widget-title-bar {
  min-height: 52px;
  background: var(--widget-title-bar-bg, #fafbfc);
  cursor: grab;
}

.widget-title-bar:active {
  cursor: grabbing;
}

.widget-icon-dot {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.widget-title-text {
  font-size: 13px;
  color: var(--widget-title-text, #3c4043);
  font-weight: 600;
}

.widget-menu-btn {
  opacity: 0;
  transition: opacity 0.15s;
}

.widget-card:hover .widget-menu-btn {
  opacity: 1;
}

/* Ghost (drag placeholder) */
.widget-ghost {
  opacity: 0.4;
  background: var(--widget-ghost-bg, #e3f2fd);
  border: 2px dashed var(--widget-ghost-border, #90caf9);
  border-radius: 16px;
}

/* Per-widget accent overrides */
.widget-web-payment-queue .widget-title-bar {
  border-top: 3px solid #f57c00;
}

.widget-cash-drawer .widget-title-bar {
  border-top: 3px solid #2e7d32;
}

.widget-gl-balance-status .widget-title-bar {
  border-top: 3px solid #00838f;
}

.widget-collections-by-category .widget-title-bar {
  border-top: 3px solid #4527a0;
}

.widget-eom-readiness .widget-title-bar {
  border-top: 3px solid #455a64;
}

/* Existing accent widgets */
.widget-verse-of-the-day .widget-title-bar {
  border-top: 3px solid #e65100;
}

.widget-announcements .widget-title-bar {
  border-top: 3px solid #1565c0;
}
</style>
